import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Crown, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function CheckoutSuccessPage() {
    useSeo({
        title: "Payment Confirmed",
        description:
            "Thank you for booking a Discovery Session with EIGOO Books. We look forward to speaking with you soon.",
    });

    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ state: "polling", tries: 0 });

    useEffect(() => {
        if (!sessionId) {
            setStatus({ state: "unknown" });
            return;
        }
        let cancelled = false;
        let tries = 0;

        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus({ state: "paid", data });
                    return;
                }
                if (["expired", "failed"].includes(data.payment_status)) {
                    setStatus({ state: "failed", data });
                    return;
                }
            } catch (_e) {
                /* keep polling */
            }
            tries += 1;
            if (tries >= 12) {
                setStatus({ state: "timeout" });
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <main
            data-testid="checkout-success-page"
            className="min-h-[80vh] flex items-center justify-center px-6 py-20"
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="w-full max-w-2xl frame-gold p-10 md:p-14 text-center"
            >
                {status.state === "polling" ? (
                    <>
                        <Loader2
                            className="w-8 h-8 text-[#D4AF37] mx-auto mb-6 animate-spin"
                            data-testid="checkout-loading"
                        />
                        <div className="eyebrow mb-3">Confirming payment</div>
                        <h1 className="font-serif text-4xl md:text-5xl text-white">
                            Sealing your session…
                        </h1>
                        <p className="text-[#A1A1AA] mt-4">
                            One moment while we verify the payment with Stripe.
                        </p>
                    </>
                ) : status.state === "paid" ? (
                    <>
                        <CheckCircle2
                            className="w-10 h-10 text-[#D4AF37] mx-auto mb-6"
                            data-testid="checkout-success-icon"
                        />
                        <div className="eyebrow mb-3">Payment confirmed</div>
                        <h1 className="font-serif text-4xl md:text-5xl leading-tight">
                            Thank you — your{" "}
                            <span className="text-gold-gradient italic">Discovery Session</span> is booked.
                        </h1>
                        <p className="text-[#cfcfcf] mt-6 max-w-lg mx-auto leading-relaxed">
                            A receipt is on its way to your inbox. Our team will reach out within one
                            business day to schedule your session at a time that suits you.
                        </p>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <Link to="/" className="btn-gold" data-testid="success-home-btn">
                                Return to the Hub <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                            <Link
                                to="/comic/sovereign-commander"
                                className="btn-ghost-gold"
                                data-testid="success-explore-btn"
                            >
                                Explore Sovereign Commander
                            </Link>
                        </div>
                    </>
                ) : status.state === "timeout" ? (
                    <>
                        <Crown className="w-8 h-8 text-[#D4AF37] mx-auto mb-6" />
                        <h1 className="font-serif text-3xl md:text-4xl text-white">
                            Still verifying with Stripe…
                        </h1>
                        <p className="text-[#A1A1AA] mt-4">
                            You'll receive a confirmation email shortly. You can safely close this page.
                        </p>
                        <Link
                            to="/"
                            className="btn-gold mt-8 inline-flex"
                            data-testid="success-home-btn"
                        >
                            Return to the Hub
                        </Link>
                    </>
                ) : (
                    <>
                        <Crown className="w-8 h-8 text-[#D4AF37] mx-auto mb-6" />
                        <h1 className="font-serif text-3xl md:text-4xl text-white">
                            Welcome back.
                        </h1>
                        <p className="text-[#A1A1AA] mt-4 max-w-md mx-auto">
                            No active session to confirm here. If you just completed a purchase,
                            check your inbox for a receipt — otherwise, feel free to explore the Hub
                            or reach out at{" "}
                            <a
                                href="mailto:hello@eigoo.books"
                                className="text-[#D4AF37] hover:text-[#E6D59A]"
                            >
                                hello@eigoo.books
                            </a>
                            .
                        </p>
                        <Link
                            to="/"
                            className="btn-ghost-gold mt-8 inline-flex"
                            data-testid="success-home-btn"
                        >
                            Return to the Hub
                        </Link>
                    </>
                )}
            </motion.div>
        </main>
    );
}
